import * as React from 'react';
import classnames from 'classnames';

import { Res } from 'core/api';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';
import { cloneExcept, deepEquals } from 'core/utils';
import { Binding, Result } from 'core/infrastructure';

import { formatValue, Format, formatAsElementId } from './Utils';

interface Props<T> extends React.HTMLProps<ComboBox<T>> {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: any | any[];
	label?: string;
	options: any[];
	format?: Format;
	multiple?: boolean;
	onValueChanged?: (values: any[]) => void;
	disableSearch?: boolean;
	formId?: string;

	// El modo tags permite al usuario añadir opciones en lugar de usar sólo
	// las opciones predefinidas. Si se quieren permitir varios o sólo uno
	// se puede seguir controlando desde la propiedad multiple
	taggable?: boolean;

	onCreate?: string | (() => void);
	onReload?: string | (() => void);
}

interface State {
	errorMsg: string;
	expanded: boolean;
}

export class ComboBox<T> extends React.Component<Props<T>, State> implements FormComponent {

	private input = React.createRef<HTMLSelectElement>();

	constructor(props: Props<T>) {
		super(props);
		this.state = {
			errorMsg: '',
			expanded: false
		};
	}

	componentDidMount() {

		this.updateSelectedValues();

		const onValueChanged = () => this.props.onValueChanged && this.props.onValueChanged(this.getSelectedValues());

		const onSelect2Close = () => this.getSelectElement().focus();

		this.getSelectElement().on('change.select2', onValueChanged);

		// Parece que hay un BUG en select2 v.4.0.3: https://github.com/select2/select2/issues/4384#issuecomment-233670141
		// Y no se mantiene el tab order una vez cerrado con Enter o Esc.
		this.getSelectElement().on('select2:close', onSelect2Close);

		$(document).on('keydown', '.select2-selection', (e: JQueryEventObject) => {

			const select = this.getSelectElement();
			// Si pulso enter con el combo abierto, quiero que se cierre y seleccione la opción,
			// pero que no burbujee el evento para que no se cierren diálogos (como el de crear albarán/pedido)
			if (e.key === 'Enter') {
				e.stopImmediatePropagation();
				e.stopPropagation();

				// Evito que se abra con Enter. Si estaba cerrado, esto hace que no se llegue a ver abierto
				// Hay que abrirlo con espacio, como el resto de cosas
				select.select2('close');
			}
		});
	}

	componentWillUnmount() {
		this.getSelectElement().off('change.select2');
		this.getSelectElement().off('select2:close');

		// A veces al compilar en producción (y sólo en prod) falla con un
		// error de `Cannot read property destroy of undefined`. Para por
		// ejemplo en restaurant al ir a imprimir etiquetas desde el listado
		// de productos y darle a volver. Intento protegerme con el if
		if (this.getSelectElement().data('select2')) {
			this.getSelectElement().select2('destroy');
		}
	}

	private findIndex(options: any[], value: any): number {
		for (let i = 0; i < options.length; i++) {
			if (deepEquals(options[i], value))
				return i;
		}
		return -1;
	}

	focus() {
		const inputNode = this.input.current!;
		inputNode.focus();
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	private getElementId() {
		return formatAsElementId(this.getPath(), this.props.formId || '');
	}

	private getSelectElement(): any {
		return $('#' + this.getElementId()) as any;
	}

	getSelectedValues() {
		const data = this.getSelectElement().select2('data');
		const selectedValues = data ? data.map((x: any) => x.id) : [];
		return this.props.taggable
			? selectedValues
			: selectedValues.map((i: any) => this.props.options[i]);
	}

	updateSelectedValues() {

		const boundValues = this.props.multiple ? (this.props.boundValue as any[]) : [this.props.boundValue];
		const selectedIndexes = boundValues.map(x => this.findIndex(this.props.options || [], x));

		const isMultiple = this.props.multiple;
		const disableSearch = this.props.disableSearch;
		const taggable = this.props.taggable;

		const candidateParentDialog = this.input.current?.closest('.modal-dialog');
		const candidateParentScreen = this.input.current?.closest('.screen');
		const candidateChakraLegacyDialog = this.input.current?.closest('.legacy-styles');

		const dropdownParent =
			candidateParentDialog ??
			candidateParentScreen ??
			candidateChakraLegacyDialog ??
			this.input.current?.parentElement;

		const config: any = {
			width: 'auto',
			placeholder: this.props.placeholder || '',
			allowClear: !this.props.required,
			templateResult: function (result: any, container: any) {
				if (!result.id) {
					return result.text;
				}

				// Necesario para fastclick en tablets y teléfonos
				container.className += ' needsclick';

				// Necesario para formatear distintos los selected en múltiple que en normal
				if (isMultiple) {
					container.className += ' multiple';
				}
				return result.text;
			}.bind(this),
			minimumResultsForSearch: disableSearch ? -1 : taggable ? 0 : 10,
			// La vinculamos a un padre para que funcione en los dialogos (La respuesta buena es la de For Select2 V4)
			// http://stackoverflow.com/questions/18487056/select2-doesnt-work-when-embedded-in-a-bootstrap-modal
			dropdownParent: dropdownParent,
			// Dejamos que autocalcule su ancho, de no ser así pueden darse casos donde la barra de scroll
			// tape las opciones y no se vean todas
			dropdownAutoWidth: true,
			multiple: isMultiple
		};

		if (this.props.taggable) {
			const isStringArray = this.props.options instanceof Array
				&& (typeof this.props.options[0] === 'undefined' || typeof this.props.options[0] === 'string');

			if (!isStringArray)
				throw new Error('Los tags en un combo deben ser de tipo string');

			config.tags = [...this.props.options];
		}

		// Por alguna razón cuando los boundValues son string no funciona con selectedIndexes
		this.getSelectElement().select2(config).val(this.props.taggable ? boundValues : selectedIndexes).trigger('change');

		this.getSelectElement().each(function (_index: any, el: any) {
			$(el).data('select2').$container.find('*').addClass('needsclick');
		});
	}

	getValue(): Result<any | any[]> {

		this.setState({ errorMsg: '' });

		const selectedValues = this.getSelectedValues();

		if (this.props.required && selectedValues.length === 0) {
			this.setState({ errorMsg: Res.Msg.MSG_MUST_SELECT_A_VALUE() });
			return Result.error();
		}

		if (this.props.multiple)
			return Result.ok(selectedValues);

		return Result.ok(selectedValues.length ? selectedValues[0] : null);
	}

	createActionLinks() {
		if (!this.props.onCreate && !this.props.onReload)
			return null;

		const actionLinksStyle: React.CSSProperties = {
			height: this.state.expanded ? 22 : 0,
			overflowY: 'hidden',
			transition: 'all 0.2s ease',
			position: 'absolute',
			marginTop: 2
		};

		const createLink = (action: string | (() => void), text: string) => {
			const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
				if (typeof action !== 'string') {
					e.preventDefault();
					action();
				}
				e.stopPropagation();
			};
			const href = typeof action === 'string' ? action : '';
			return <a tabIndex={-1} href={href} className='link' onClick={onClick} target='_blank' rel="noreferrer">{text}</a>;
		};

		const create = this.props.onCreate ? createLink(this.props.onCreate, Res.WA.WA_CREATE_LNK()) : null;
		const reload = this.props.onReload ? createLink(this.props.onReload, Res.WA.WA_RELOAD_LNK()) : null;
		const separator = create && reload ? ' | ' : null;

		return <div style={actionLinksStyle}>
			{create}{separator}{reload}
		</div>;
	}

	createExpandIcon() {
		if (!this.props.onCreate && !this.props.onReload)
			return null;

		const iconStyle: React.CSSProperties = {
			fontSize: 10,
			marginLeft: -14,
			paddingRight: 4,
			position: 'relative'
		};

		const iconClass = this.state.expanded
			? 'text-info glyphicon glyphicon-chevron-down'
			: 'text-info glyphicon glyphicon-chevron-right';

		return <span className={iconClass} style={iconStyle}></span>;
	}

	render() {

		const props: any = cloneExcept(this.props, [
			'linkTo', 'boundValue', 'label', 'options', 'format', 'allowEmpty', 'multiple', 'onValueChanged', 'ref', 'disableSearch', 'formId', 'taggable', 'onCreate', 'onReload', 'renderOption']);

		const options = this.props.taggable ? [] : (this.props.options || []).map((x, i) => {
			const formattedValue = formatValue(x, this.props.format);
			return <option key={i + formattedValue} value={i}>{formattedValue}</option>;
		});

		const inputId = this.getElementId();

		const input = <select id={inputId} ref={this.input} className='select2 form-control' multiple={this.props.multiple} {...props}>{options}</select>;

		const children = React.Children.map(
			this.props.children || [],
			(c: any) => React.cloneElement(c, { disabled: this.props.disabled }));

		const inputGroup = children?.length
			? <div className='input-group'>{input} {children}</div>
			: input;

		const labelClick = (e: React.MouseEvent<HTMLLabelElement>) => {
			this.setState({ expanded: !this.state.expanded });
			e.preventDefault();
			e.stopPropagation();
		};

		const label = this.props.label
			? <label className='col-sm-4 control-label' onClick={labelClick}>
				{this.createExpandIcon()}{this.props.label || ''}
				{this.createActionLinks()}
			</label>
			: null;

		const inputClasses = classnames({ 'col-sm-8': !!label, 'col-sm-12': !label });

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{label}
			<div className={inputClasses}>
				{inputGroup}
				<ErrorBlock msg={this.state.errorMsg} />
			</div>

		</div>;
	}
}

export type ComboBoxCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
